import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { refreshTokenV2 } from "../services/api";
import { baseURL } from "../services/axiosConfig";
import DropDownArrow from "../Asserts/images/black_dropdown.svg";
import { useDispatch, useSelector } from "react-redux";
import useWebSocket from "../websoket/websocket";
import { fetchFixturesDetails } from "./fixturesAndResult.slice";
import { use } from "react";


const isCheckStatusLive = status => !['FT', 'AET', 'PEN', 'NS'].includes(status);

const ChevronDown = ({ className = "" }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className={className}>
    <path d="M6 8L10 12L14 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function formatMatchTime(dateString) {
  const date = new Date(dateString);
  let hours = date.getHours();
  let mins = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${mins.toString().padStart(2, "0")} ${ampm}`;
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const formatted = date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
  return formatted.replace(/^(\w+) /, '$1, ');
}

const statusBadge = (status) => {
  if (status === "FT") {
    return <span className="bg-[#37c570] text-white text-[11px] px-2 py-[2px] rounded-full font-semibold">FT</span>;
  }
  if (isCheckStatusLive(status)) {
    return (
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 bg-[#37c570] rounded-full"></span>
        <span className="bg-[#37c570] text-white text-[11px] px-2 py-[2px] rounded-full font-semibold">
          LIVE
        </span>
      </div>
    );
  }
  return null;
};

function Dropdown({ options, value, onChange, className = "", placeholder = "Select", disabled = false, showAllOption = false }) {
  const [open, setOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (open && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 224; // max-h-56 in pixels (14rem = 224px)

      // If there's not enough space below and more space above, show dropdown upward
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    }
  }, [open]);

  // Create options array with "All" option at the beginning if showAllOption is true
  const allOptions = showAllOption
    ? [{ value: "", label: "All Leagues", fullLabel: "All Leagues" }, ...options]
    : options;

  // Find the current selected option (including "All" if applicable)
  const selectedOption = allOptions.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative w-full sm:w-auto ${className}`}>
      <button
        type="button"
        className={`
          flex items-center justify-between px-2 sm:px-4 h-9 sm:h-11 rounded-md bg-white border border-[#d9d9d9]
          text-xs sm:text-base font-medium text-[#222] min-w-[100px] sm:min-w-[185px] w-full sm:w-auto
          focus:outline-none transition shadow font-inter
          ${open ? "ring-2 ring-blue-200" : ""}
          ${disabled ? "cursor-not-allowed opacity-50" : ""}
        `}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className="truncate sm:hidden">
          {selectedOption?.truncatedLabel ||
            selectedOption?.label ||
            placeholder}
        </span>
        <span className="truncate hidden sm:block">
          {selectedOption?.fullLabel ||
            selectedOption?.label ||
            placeholder}
        </span>
        <img src={DropDownArrow} alt="Dropdown Arrow" className={`ml-2 w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && allOptions.length > 0 && !disabled && (
        <ul
          ref={dropdownRef}
          className={`
            absolute z-[9999] bg-white border border-[#E0E6F0] rounded-lg shadow-lg max-h-56 overflow-auto
            ${dropdownDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"}
            w-full
          `}
        >
          {allOptions.map((opt) => (
            <li
              key={opt.value}
              onClick={() => {
                if (opt.value === "no-leagues" || opt.value === "no-weeks")
                  return;
                onChange(opt.value);
                setOpen(false);
              }}
              className={`px-2 sm:px-4 py-1 sm:py-2 cursor-pointer hover:bg-[#F5F7FA] text-[#222] text-xs sm:text-base ${opt.value === value ? "bg-[#F5F7FA]" : ""
                } ${opt.value === "no-leagues" || opt.value === "no-weeks"
                  ? "text-gray-400 cursor-not-allowed hover:bg-transparent"
                  : ""
                }`}
            >
              {opt.fullLabel || opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MatchCard = ({ fixture }) => (
  <div className="flex items-center justify-between min-w-[320px] sm:min-w-[400px] max-w-[550px] flex-shrink px-2 sm:px-0">
    {/* Home Team */}
    <div className="flex flex-col items-center flex-1">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white border border-[#ececec] flex items-center justify-center mb-1.5 overflow-hidden">
        {fixture?.hometeam_logo ? (
          <img
            src={fixture.hometeam_logo}
            alt={fixture.hometeam}
            className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
          />
        ) : (
          <span className="font-bold text-[#222] text-sm sm:text-lg">
            {fixture?.short?.find(s => s.team_type === "home")?.code || fixture?.hometeam || "HOME"}
          </span>
        )}
      </div>
      <span className="mt-1 text-xs sm:text-sm font-semibold text-[#222] text-center">
        {fixture?.short?.find(s => s.team_type === "home")?.code || fixture?.hometeam || "HOME"}
      </span>
    </div>

    {/* Score / Time */}
    <div className="flex flex-col items-center min-w-[60px] sm:min-w-[80px] mx-2 sm:mx-4">
      {(fixture?.status === "FT" || isCheckStatusLive(fixture?.status)) ? (
        <>
          <span className="text-lg sm:text-[22px] font-bold text-[#222] mb-1 leading-none">
            {fixture.hometeam_goal} - {fixture.awayteam_goal}
          </span>
          {/* {statusBadge(fixture.status)} */}
          <>
            {
              isCheckStatusLive(fixture?.status) && <span
                className={`text-white bg-[#37c570] p-2 text-xs px-3 py-1 
                                    rounded-full flex items-center gap-1 font-semibold`}
                style={{
                  backgroundColor: '#37c570',
                  color: "white"
                }}
              >
                <span
                  className="h-2 w-2 bg-white rounded-full mr-1"
                  style={{
                    display: "inline-block",
                    backgroundColor: "white",
                    color: "white"
                  }}
                ></span>
                Live
              </span>
            }
          </>
        </>
      ) : (
        <span className="text-sm sm:text-base font-semibold text-[#222] whitespace-nowrap">
          {formatMatchTime(fixture.start)}
        </span>
      )}
    </div>

    {/* Away Team */}
    <div className="flex flex-col items-center flex-1">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white border border-[#ececec] flex items-center justify-center mb-1.5 overflow-hidden">
        {fixture?.awayteam_logo ? (
          <img
            src={fixture.awayteam_logo}
            alt={fixture.awayteam}
            className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
          />
        ) : (
          <span className="font-bold text-[#222] text-sm sm:text-lg">
            {fixture?.short?.find(s => s.team_type === "away")?.code || fixture?.awayteam || "AWAY"}
          </span>
        )}
      </div>
      <span className="mt-1 text-xs sm:text-sm font-semibold text-[#222] text-center">
        {fixture?.short?.find(s => s.team_type === "away")?.code || fixture?.awayteam || "AWAY"}
      </span>
    </div>
  </div>
);

const FixturesAndResults = () => {
  const dispatch = useDispatch();
  const socketRef = useWebSocket("/ws/livefixture/");
  const rawToken = localStorage.getItem("access_token");
  const token = (rawToken && rawToken !== "null" && rawToken !== "undefined") ? JSON.parse(rawToken) : null;
  
  const [fixtures, setFixtures] = useState([]);
  const [matchWeekList, setMatchWeekList] = useState([]);
  const [leagueList, setLeaguesList] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [count, setCount] = useState(0);

  // Helper function to build query params
  const buildQueryParams = (selectedLeague, selectedWeek, leagueList, page) => {
    const queryParams = new URLSearchParams();

    if (selectedLeague && selectedLeague !== "") {
      queryParams.append("league", selectedLeague);

      const selectedLeagueObj = leagueList.find(league => league.id === selectedLeague);
      if (selectedLeagueObj && selectedLeagueObj.season_year) {
        queryParams.append("season", selectedLeagueObj.season_year);
      }
    }

    if (selectedWeek && selectedWeek !== "") {
      queryParams.append("matchweek", selectedWeek);
    }

    queryParams.append("paginated", true);
    queryParams.append("group_by_date", true);
    queryParams.append("page", page);

    return queryParams;
  };

  const fetchMatchWeeks = async (leagueId) => {
    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const url = `${baseURL}/pot/seasonmatchweeks?season=${leagueId}`;
      const response = await axios.get(url, { headers });
      setMatchWeekList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching match weeks:", error);
      setMatchWeekList([]);
    }
  };

  const fetchLeagues = async () => {
    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await axios.get(`${baseURL}/pot/seasons`, { headers });
      setLeaguesList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching leagues:", error);
    }
  };

  useEffect(() => {
    fetchLeagues();
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedLeague !== "") {
      fetchMatchWeeks(selectedLeague);
      setSelectedWeek("");
    } else {
      setMatchWeekList([]);
      setSelectedWeek("");
    }
    // Reset pagination when league changes
    setCurrentPage(1);
    setHasMoreData(true);
  }, [selectedLeague]);

  // Reset pagination when week changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreData(true);
  }, [selectedWeek]);

  // Main effect to fetch fixtures when filters change
  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        setIsLoading(true);
        const queryParams = buildQueryParams(selectedLeague, selectedWeek, leagueList, 1); // Always use page 1 for filter changes
        const queryString = queryParams.toString();
        const url = `${baseURL}/pot/fixtures${queryString ? `?${queryString}` : ''}`;

        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get(url, { headers });
        const newData = response?.data?.data || response?.data || [];
        dispatch(fetchFixturesDetails(newData));

        setHasMoreData(newData.length > 0);
        setCurrentPage(1); // Reset current page to 1

      } catch (error) {
        console.error("Error fetching fixtures data:", error);
        if (error.response && error.response.status === 401 && token) {
          refreshTokenV2(dispatch);
        }
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchFixtures();

  }, [selectedLeague, selectedWeek, token, leagueList]);

  const fetchFixturesDetail = useSelector(state => state.fixturesAndResults.detail);

  useEffect(() => {
    if (fetchFixturesDetail.length > 0) {
      setFixtures(fetchFixturesDetail);
    }
  }, [fetchFixturesDetail]);

  // Load more fixtures function
  const loadMoreFixtures = async () => {
    if (isLoadingMore || !hasMoreData || fixtures.length === 0) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    try {
      setIsLoadingMore(true);

      const queryParams = buildQueryParams(selectedLeague, selectedWeek, leagueList, nextPage);
      const queryString = queryParams.toString();
      const url = `${baseURL}/pot/fixtures${queryString ? `?${queryString}` : ''}`;

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(url, { headers });

      const newData = response?.data?.data || response?.data || [];

      if (newData.length > 0) {
        dispatch(fetchFixturesDetails([...fixtures, ...newData]));
      } else {
        setHasMoreData(false);
      }

    } catch (error) {
      console.error("Error loading more fixtures:", error);
      if (error.response && error.response.status === 401 && token) {
        refreshTokenV2(dispatch);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) {
        return;
      }

      if (fixtures.length > 0 && hasMoreData && !isLoadingMore && !isLoading) {
        loadMoreFixtures();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixtures.length, hasMoreData, isLoadingMore, isLoading, currentPage, selectedLeague, selectedWeek]);

  const getLeagueOptions = () => {
    if (leagueList.length === 0) {
      return [
        { value: "no-leagues", label: "No leagues available" },
      ];
    }
    const dynamicOptions = leagueList?.map((league) => {
      const originalLabel = league?.leaguename || league.label;
      return {
        value: league?.id || league.value,
        label: originalLabel,
        fullLabel: originalLabel,
        truncatedLabel:
          originalLabel && originalLabel.length > 10
            ? `${originalLabel.substring(0, 10)}...`
            : originalLabel,
      };
    });
    return dynamicOptions;
  };

  const getMatchWeekOptions = () => {
    // If no league is selected, return empty array
    if (selectedLeague === "") {
      return [];
    }

    if (matchWeekList?.length === 0) {
      return [
        { value: "no-weeks", label: "No match weeks available" },
      ];
    }

    const dynamicOptions = matchWeekList?.map((week) => {
      let num = week?.matchweek.split("-")[1];
      return {
        value: week?.id || week?.value,
        label: `Match Week - ${num}` || `Match Week ${week.number || week.week_number || week.id}`
      }
    });
    return dynamicOptions;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sendTestMessage = () => {
    if (WebSocket.OPEN) {
      console.log("WebSocket is open");
    } else {
      console.log("WebSocket is not open");
    }
  };

  useEffect(() => {
    sendTestMessage()
  }, [])

  return (
    <div className="w-[100%] min-h-screen bg-[#fff] pb-8">
      <div className="w-full sm:max-w-[95.5%] mx-auto pt-6 sm:pt-8 px-2 md:px-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-0 gap-4">
          <h2 className="text-xl sm:text-3xl font-bold text-black font-archivo mb-0">
            Fixtures & Results
          </h2>
          <div className="flex flex-row gap-3">
            <Dropdown
              options={getLeagueOptions()}
              value={selectedLeague}
              onChange={setSelectedLeague}
              placeholder="Select League"
              className="flex-1 sm:flex-none sm:mr-3"
              showAllOption={true}
            />
            <Dropdown
              options={getMatchWeekOptions()}
              value={selectedWeek}
              onChange={selectedLeague !== "" ? setSelectedWeek : () => { }}
              placeholder="Select Match Week"
              disabled={selectedLeague === ""}
              className={`flex-1 sm:flex-none ${selectedLeague === "" ? "opacity-50 cursor-not-allowed" : ""}`}
            />
          </div>
        </div>

        <div className="border-t-2 border-dashed border-[#ddd] my-6"></div>

        <div className="w-full">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#0081F8] rounded-full animate-spin"></div>
              </div>
              <p className="mt-4 text-gray-600 text-lg font-medium">Loading fixtures...</p>
            </div>
          ) : fixtures && fixtures.length > 0 ? (
            fixtures?.map((day, dayIdx) => (
              <div key={day.date} className={dayIdx > 0 ? "mt-12" : ""}>
                <div className="text-lg md:text-xl font-bold mb-10 text-[#111]">
                  {formatDate(day.date)}
                </div>
                <div className="w-full overflow-x-auto scrollbar-hide lg:hidden">
                  <div className="flex gap-6 pb-2 min-w-max justify-center sm:justify-start">
                    {day?.fixtures?.map((fixture, idx) => (
                      <React.Fragment key={fixture.id}>
                        <MatchCard fixture={fixture} />
                        {idx < day?.fixtures?.length - 1 && (
                          <div className="flex items-center justify-center flex-shrink-0">
                            <div className="w-[1px] h-20 bg-[#ededed]"></div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className="hidden lg:block">
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-x-6 gap-y-12 justify-items-center">
                    {day?.fixtures?.map((fixture, idx) => (
                      <React.Fragment key={fixture.id}>
                        <div className="w-full max-w-[550px] flex items-center gap-6">
                          <MatchCard fixture={fixture} />
                          {(idx + 1) % 3 !== 0 && idx < day?.fixtures?.length - 1 && (
                            <div className="hidden xl:flex items-center justify-center flex-shrink-0">
                              <div className="w-[1px] h-20 bg-[#ededed]"></div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-gray-400 text-center">
                <div className="text-xl mb-2">No fixtures found</div>
                <p className="text-sm">Try selecting a different league or match week</p>
              </div>
            </div>
          )}

          {/* Loading more indicator */}
          {isLoadingMore && fixtures.length > 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative">
                <div className="w-8 h-8 border-4 border-gray-200 border-t-[#0081F8] rounded-full animate-spin"></div>
              </div>
              <p className="mt-2 text-gray-600 text-sm font-medium">Loading more fixtures...</p>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .font-archivo {
          font-family: 'Archivo', 'Helvetica Neue', Arial, 'sans-serif';
        }
      `}</style>
    </div>
  );
};

export default FixturesAndResults;